import type { ClimateTelemetry } from '../worker/climate-protocol';

interface Sample { time: number; temperature: number; albedo: number }

/** Bounded histories for every body, sampled by simulation time, not render FPS. */
export class ClimateHistory {
  public readonly samples = new Map<number, Sample[]>();
  public revision = 0;
  private lastTime = -1;
  private lastSample = -Infinity;

  public accept(time: number, bodies: readonly ClimateTelemetry[], reset = false): void {
    if (reset || time < this.lastTime) {
      this.samples.clear();
      this.lastSample = -Infinity;
    }
    this.lastTime = time;
    if (time - this.lastSample < 2) return;
    this.revision += 1;
    this.lastSample = time;
    for (const body of bodies) {
      const history = this.samples.get(body.index) ?? [];
      history.push({ time, temperature: body.temperature, albedo: body.albedo });
      while (history.length > 301 || (history[0]?.time ?? time) < time - 600) history.shift();
      this.samples.set(body.index, history);
    }
  }
}

export class ClimateInspector {
  private readonly panel = document.getElementById('climate-panel')!;
  private readonly history = new ClimateHistory();
  private bodies: readonly ClimateTelemetry[] = [];
  private revision = 0;
  private lastKey = '';
  private lastHistoryKey = '';
  private readonly elements = new Map([
    'climate-name', 'climate-readings', 'climate-thermal', 'climate-materials', 'climate-phases',
    'climate-inventory', 'climate-history-time', 'temperature-history',
    'albedo-history', 'temperature-label', 'albedo-label',
  ].map(id => [id, document.getElementById(id)!]));

  public accept(time: number, bodies: readonly ClimateTelemetry[], reset: boolean): void {
    this.bodies = bodies;
    this.history.accept(time, bodies, reset);
    this.revision += 1;
  }

  public show(cameraMode: string): void {
    const index = cameraMode.startsWith('body-') ? Number(cameraMode.slice(5)) : -1;
    const body = this.bodies.find(candidate => candidate.index === index);
    this.panel.hidden = body === undefined;
    if (body === undefined) { this.lastKey = ''; this.lastHistoryKey = ''; return; }
    const key = `${index}:${this.revision}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.text('climate-name', body.name);
    this.text('climate-thermal', formatLayerTemperatures(body));
    this.text('climate-readings', `${body.temperature.toFixed(1)} K · Albedo ${body.albedo.toFixed(2)} · Clouds ${(body.cloudCover * 100).toFixed(0)}%${body.fusion ? '' : ` · ${body.pressure.toPrecision(3)} bar`}${body.hasSurface ? ` · Liquid ${(body.liquidCoverage * 100).toFixed(0)}% · Level ${body.seaLevelMeters.toFixed(1)} m` : ''}`);
    this.text('climate-materials', body.fusion ? 'Hydrogen–helium fusion · fixed bulk reservoirs' :
      `${formatAtmosphere(body) || 'No substantial gas phase'}${body.dominantLiquid ? ` · ${body.dominantLiquid} liquid` : ''}`);
    this.text('climate-phases', ['Solid', 'Liquid', 'Gas', 'Plasma'].map((name, phase) => `${name} ${((body.phases[phase] ?? 0) * 100).toFixed(0)}%`).join(' · '));
    this.text('climate-inventory', formatInventory(body));
    const historyKey = `${index}:${this.history.revision}`;
    if (historyKey === this.lastHistoryKey) return;
    this.lastHistoryKey = historyKey;
    const samples = this.history.samples.get(index) ?? [];
    const end = samples.at(-1)?.time ?? 0;
    const start = samples[0]?.time ?? end;
    this.plot('temperature', samples, start, end);
    this.plot('albedo', samples, start, end);
    this.text('climate-history-time', `Last ${Math.round(end - start)} simulated seconds · up to 10 min`);
  }

  private plot(field: 'temperature' | 'albedo', samples: Sample[], start: number, end: number): void {
    const values = samples.map(sample => sample[field]);
    const minimum = field === 'albedo' ? 0 : Math.min(...values);
    const maximum = field === 'albedo' ? 1 : Math.max(...values);
    const low = Number.isFinite(minimum) ? minimum : 0;
    const high = Number.isFinite(maximum) ? maximum : 1;
    const span = Math.max(field === 'albedo' ? 1 : 2, high - low);
    const offset = field === 'albedo' ? 0 : low - (span - (high - low)) * 0.5;
    const points = samples.map(sample => `${(2 + (sample.time - start) / Math.max(2, end - start) * 276).toFixed(1)},${(46 - (sample[field] - offset) / span * 44).toFixed(1)}`);
    if (points.length === 1) points.push(points[0]!.replace(/^[^,]+/, '4'));
    this.elements.get(`${field}-history`)!.setAttribute('points', points.join(' '));
    this.text(`${field}-label`, field === 'temperature' ? `Temperature · ${offset.toFixed(1)}–${(offset + span).toFixed(1)} K` : 'Albedo · 0–1');
  }

  private text(id: string, value: string): void {
    const element = this.elements.get(id)!;
    if (element.textContent !== value) element.textContent = value;
  }
}

const PHASE_NAMES = ['solid', 'liquid', 'gas', 'plasma'] as const;

export function formatLayerTemperatures(body: ClimateTelemetry): string {
  const names = { atmosphere: body.fusion ? 'Plasma' : 'Air/clouds', surface: 'Liquids/ice', crust: 'Crust' };
  return (Object.keys(names) as (keyof typeof names)[]).flatMap(layer => {
    const temperature = body.layerTemperatures[layer];
    return temperature === null ? [] : [`${names[layer]} ${temperature.toFixed(1)} K`];
  }).join(' · ');
}

export function formatAtmosphere(body: ClimateTelemetry): string {
  const gasTotal = body.materials.reduce((sum, stock) => sum + stock.phases[2], 0);
  return body.materials.filter(stock => stock.phases[2] > 1e-8)
    .sort((a, b) => b.phases[2] - a.phases[2]).slice(0, 3)
    .map(stock => `${stock.name} ${(100 * stock.phases[2] / Math.max(1e-12, gasTotal)).toFixed(0)}%`).join(' · ');
}

export function formatInventory(body: ClimateTelemetry): string {
  return body.materials.map(stock => `${stock.name}: ${stock.phases.flatMap((amount, phase) =>
    amount > 1e-10 ? [`${amount.toPrecision(3)} ${PHASE_NAMES[phase]}`] : []).join(', ')}`).join('\n');
}
